import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiClient } from "../../lib/api/client.js";
import { createUtenteSchema, updateUtenteSchema, type UtenteAmministrato, type CreateUtente } from "@taras/contracts";

type UpdateUtente = z.infer<typeof updateUtenteSchema>;

const RUOLO_LABELS: Record<string, string> = {
  ADMIN: "Amministratore",
  BACKOFFICE_COMUNE: "Backoffice Comune",
  BACKOFFICE_GESTORE: "Backoffice Gestore",
  OPERATORE: "Operatore",
  VISUALIZZATORE: "Visualizzatore",
};

export function AdminPage() {
  const [openNew, setOpenNew] = useState(false);
  const [editUtente, setEditUtente] = useState<UtenteAmministrato | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<{ items: UtenteAmministrato[]; next_cursor: string | null }>({
    queryKey: ["admin", "utenti"],
    queryFn: () => apiClient.get("/api/v1/admin/utenti"),
  });

  // ── Crea ──
  const createForm = useForm<CreateUtente>({
    resolver: zodResolver(createUtenteSchema),
    defaultValues: { ruolo: "OPERATORE" },
  });
  const createMutation = useMutation({
    mutationFn: (body: CreateUtente) => apiClient.post<UtenteAmministrato>("/api/v1/admin/utenti", body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["admin", "utenti"] }); createForm.reset(); setOpenNew(false); },
  });

  // ── Modifica ──
  const editForm = useForm<UpdateUtente>({ resolver: zodResolver(updateUtenteSchema) });
  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateUtente }) =>
      apiClient.patch<UtenteAmministrato>(`/api/v1/admin/utenti/${id}`, body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["admin", "utenti"] }); setEditUtente(null); },
  });

  // ── Reset password ──
  const resetPwd = useMutation({
    mutationFn: (id: string) => apiClient.post<{ temporary_password: string }>(`/api/v1/admin/utenti/${id}/reset-password`, {}),
  });

  const openEdit = (u: UtenteAmministrato) => {
    setEditUtente(u);
    editForm.reset({ nome: u.nome, cognome: u.cognome, email: u.email ?? "", ruolo: u.ruolo, attivo: u.attivo });
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4">
        <h2 className="text-lg font-semibold text-gray-900">Gestione Utenti</h2>
        <button onClick={() => setOpenNew(true)} className="ml-auto px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors">
          Nuovo utente
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
      {isLoading ? (
        <div className="text-sm text-gray-500">Caricamento...</div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className={th}>Username</th>
                <th className={th}>Nome</th>
                <th className={th}>Ruolo</th>
                <th className={th}>MFA</th>
                <th className={th}>Stato</th>
                <th className={th}>Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data?.items.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs font-medium">{u.username}</td>
                  <td className="px-4 py-3">{u.cognome} {u.nome}</td>
                  <td className="px-4 py-3 text-gray-600 text-xs">{RUOLO_LABELS[u.ruolo] ?? u.ruolo}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${u.mfa_enabled ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {u.mfa_enabled ? "Attiva" : "Disattiva"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${u.attivo ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                      {u.attivo ? "Attivo" : "Disattivato"}
                    </span>
                  </td>
                  <td className="px-4 py-3 flex gap-3">
                    <button onClick={() => openEdit(u)} className="text-xs text-blue-600 hover:underline">Modifica</button>
                    <button
                      onClick={() => { if (confirm(`Reset password per ${u.username}?`)) resetPwd.mutate(u.id); }}
                      className="text-xs text-gray-500 hover:underline"
                    >
                      Reset pwd
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reset pwd feedback */}
      {resetPwd.isSuccess && resetPwd.data && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 text-sm">
          Password temporanea: <span className="font-mono font-semibold">{(resetPwd.data as { temporary_password: string }).temporary_password}</span>
          <button onClick={() => resetPwd.reset()} className="ml-3 text-xs text-gray-500 hover:underline">×</button>
        </div>
      )}
      </div>

      {/* Modal: Nuovo utente */}
      {openNew && (
        <ModalWrap title="Nuovo utente" onClose={() => { setOpenNew(false); createForm.reset(); }}>
          <form onSubmit={createForm.handleSubmit((d) => createMutation.mutate(d))} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nome" error={createForm.formState.errors.nome?.message}>
                <input {...createForm.register("nome")} className={input} placeholder="Mario" />
              </Field>
              <Field label="Cognome" error={createForm.formState.errors.cognome?.message}>
                <input {...createForm.register("cognome")} className={input} placeholder="Rossi" />
              </Field>
            </div>
            <Field label="Username" error={createForm.formState.errors.username?.message}>
              <input {...createForm.register("username")} className={input} placeholder="m.rossi" />
            </Field>
            <Field label="Email" error={createForm.formState.errors.email?.message}>
              <input type="email" {...createForm.register("email")} className={input} placeholder="opzionale" />
            </Field>
            <Field label="Ruolo" error={createForm.formState.errors.ruolo?.message}>
              <select {...createForm.register("ruolo")} className={input}>
                <option value="OPERATORE">Operatore</option>
                <option value="VISUALIZZATORE">Visualizzatore</option>
                <option value="BACKOFFICE_GESTORE">Backoffice Gestore</option>
                <option value="BACKOFFICE_COMUNE">Backoffice Comune</option>
                <option value="ADMIN">Amministratore</option>
              </select>
            </Field>
            <Field label="Password iniziale" error={createForm.formState.errors.password?.message}>
              <input type="password" {...createForm.register("password")} className={input} placeholder="min. 12 caratteri" />
            </Field>
            {createMutation.isError && <p className="text-xs text-red-600">Errore durante la creazione.</p>}
            <Buttons onCancel={() => { setOpenNew(false); createForm.reset(); }} loading={createMutation.isPending} label="Crea utente" />
          </form>
        </ModalWrap>
      )}

      {/* Modal: Modifica utente */}
      {editUtente && (
        <ModalWrap title={`Modifica — ${editUtente.username}`} onClose={() => setEditUtente(null)}>
          <form onSubmit={editForm.handleSubmit((d) => updateMutation.mutate({ id: editUtente.id, body: d }))} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nome" error={editForm.formState.errors.nome?.message}>
                <input {...editForm.register("nome")} className={input} />
              </Field>
              <Field label="Cognome" error={editForm.formState.errors.cognome?.message}>
                <input {...editForm.register("cognome")} className={input} />
              </Field>
            </div>
            <Field label="Email" error={editForm.formState.errors.email?.message}>
              <input type="email" {...editForm.register("email")} className={input} placeholder="opzionale" />
            </Field>
            <Field label="Ruolo">
              <select {...editForm.register("ruolo")} className={input}>
                <option value="OPERATORE">Operatore</option>
                <option value="VISUALIZZATORE">Visualizzatore</option>
                <option value="BACKOFFICE_GESTORE">Backoffice Gestore</option>
                <option value="BACKOFFICE_COMUNE">Backoffice Comune</option>
                <option value="ADMIN">Amministratore</option>
              </select>
            </Field>
            <Field label="Stato">
              <select {...editForm.register("attivo", { setValueAs: (v) => v === "true" || v === true })} className={input}>
                <option value="true">Attivo</option>
                <option value="false">Disattivato</option>
              </select>
            </Field>
            {updateMutation.isError && <p className="text-xs text-red-600">Errore durante il salvataggio.</p>}
            <Buttons onCancel={() => setEditUtente(null)} loading={updateMutation.isPending} label="Salva" />
          </form>
        </ModalWrap>
      )}
    </div>
  );
}

// ─── Shared ───────────────────────────────────────────────────────────────────

const input = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const th = "px-4 py-3 text-left font-medium text-gray-600";

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

function Buttons({ onCancel, loading, label }: { onCancel: () => void; loading: boolean; label: string }) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Annulla</button>
      <button type="submit" disabled={loading} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400">
        {loading ? "Salvataggio…" : label}
      </button>
    </div>
  );
}

function ModalWrap({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>
        {children}
      </div>
    </div>
  );
}
